import { useState, useMemo, useRef } from "react";

import { Eye, Code } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";
import { CopyableCell } from "@/components/ui/copyable-cell";

/* ── Warp-style coral accent ── */
const CORAL = "#e8796e";

type ViewMode = "raw" | "rendered";

/** Strip YAML frontmatter (--- ... ---) from markdown content */
function stripFrontmatter(text: string): string {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith("---")) return text;
  const end = trimmed.indexOf("---", 3);
  if (end === -1) return text;
  return trimmed.slice(end + 3).trimStart();
}

interface MarkdownViewerProps {
  content: string;
  className?: string;
}

/**
 * Warp-style markdown viewer with Raw / Rendered toggle.
 * Read-only — for editable version see custom-skill-editor.tsx.
 */
export function MarkdownViewer({ content, className }: MarkdownViewerProps) {
  const [mode, setMode] = useState<ViewMode>("rendered");
  const scrollRef = useRef<HTMLDivElement>(null);

  const lineCount = useMemo(() => {
    if (!content) return 1;
    return content.split("\n").length;
  }, [content]);

  const renderedContent = useMemo(() => stripFrontmatter(content), [content]);

  return (
    <div className={cn("rounded-xl border border-border/60 overflow-hidden shadow-[0_2px_16px_-4px_hsl(0_0%_0%/0.12)]", className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/40 bg-muted/20">
        <span className="text-[11px] font-mono font-medium text-muted-foreground/60">SKILL.md</span>
        <div className="flex items-center gap-2">
          <div className="flex items-center h-9 p-1 bg-accent dark:bg-white/[0.06] rounded-md gap-0.5">
            <button
              type="button"
              onClick={() => setMode("rendered")}
              className={cn(
                "flex items-center gap-1.5 px-2.5 h-full rounded-md text-xs font-medium transition-colors",
                mode === "rendered"
                  ? "bg-foreground/90 text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Eye className="h-3 w-3" />
              Rendered
            </button>
            <button
              type="button"
              onClick={() => setMode("raw")}
              className={cn(
                "flex items-center gap-1.5 px-2.5 h-full rounded-md text-xs font-medium transition-colors",
                mode === "raw"
                  ? "bg-foreground/90 text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Code className="h-3 w-3" />
              Raw
            </button>
          </div>
          <CopyableCell
            value={content}
            display={null}
            tooltipLabel="Copy SKILL.md"
            iconSize={14}
          />
        </div>
      </div>

      {/* Content */}
      <div ref={scrollRef} className="overflow-auto bg-[hsl(var(--card))] flex-1" style={{ maxHeight: "calc(100vh - 280px)" }}>
        {mode === "raw" ? (
          /* ── Raw view with line numbers ── */
          <div className="flex min-h-full">
            <div
              className="select-none sticky left-0 shrink-0 pt-4 pb-4 pr-2 pl-3 text-right border-r border-border/30 bg-[hsl(var(--card))]"
              style={{ width: 48 }}
              aria-hidden="true"
            >
              {Array.from({ length: Math.max(lineCount, 1) }, (_, i) => (
                <div key={i} className="font-mono text-[13px] leading-[1.7] text-muted-foreground/30">
                  {i + 1}
                </div>
              ))}
            </div>
            <pre className="flex-1 p-4 font-mono text-[13px] leading-[1.7] text-foreground/85 whitespace-pre-wrap break-words">
              {content}
            </pre>
          </div>
        ) : (
          /* ── Rendered view (Warp-style) ── */
          <div className="px-8 py-6">
            {renderedContent.trim() ? (
              <div className="max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-[26px] font-bold text-foreground mt-0 mb-5 leading-tight">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-[20px] font-bold mt-8 mb-3 leading-snug" style={{ color: CORAL }}>
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-[16px] font-semibold mt-6 mb-2 leading-snug text-foreground/90">
                        {children}
                      </h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-[14px] font-semibold mt-5 mb-1.5 text-foreground/80">
                        {children}
                      </h4>
                    ),
                    h5: ({ children }) => (
                      <h5 className="text-[13px] font-semibold mt-4 mb-1 text-foreground/75">
                        {children}
                      </h5>
                    ),
                    h6: ({ children }) => (
                      <h6 className="text-[12px] font-semibold mt-3 mb-1 text-muted-foreground uppercase tracking-wide">
                        {children}
                      </h6>
                    ),
                    p: ({ children }) => (
                      <p className="text-[14px] leading-[1.7] text-foreground/80 my-2.5">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-bold text-foreground">{children}</strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-foreground/70">{children}</em>
                    ),
                    del: ({ children }) => (
                      <del className="text-foreground/50 line-through">{children}</del>
                    ),
                    ul: ({ children, className: cls }) => (
                      <ul className={cn(
                        "my-2 space-y-1",
                        cls?.includes("contains-task-list") ? "pl-0 list-none" : "pl-5 list-disc",
                      )}>
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="my-2 space-y-1 pl-5 list-decimal">
                        {children}
                      </ol>
                    ),
                    li: ({ children, className: cls }) => {
                      const isTask = cls?.includes("task-list-item");
                      return (
                        <li className={cn(
                          "text-[14px] leading-[1.7] text-foreground/80",
                          isTask && "flex items-start gap-2 list-none",
                        )}>
                          {children}
                        </li>
                      );
                    },
                    input: ({ type, checked, ...props }) => {
                      if (type === "checkbox") {
                        return (
                          <input
                            type="checkbox"
                            checked={checked ?? false}
                            disabled
                            className="mt-[5px] shrink-0 cursor-default"
                            style={{ width: 14, height: 14, appearance: "none", WebkitAppearance: "none", border: "1.5px solid hsl(var(--foreground) / 0.3)", borderRadius: 2, backgroundColor: checked ? CORAL : "transparent", borderColor: checked ? CORAL : undefined }}
                            {...props}
                          />
                        );
                      }
                      return <input type={type} {...props} />;
                    },
                    code: ({ children, className: cls }) => {
                      const isBlock = cls?.includes("language-");
                      if (isBlock) {
                        return (
                          <code className="block font-mono text-[12.5px] leading-relaxed text-foreground/80">
                            {children}
                          </code>
                        );
                      }
                      return (
                        <code
                          className="font-mono text-[12.5px] px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: `${CORAL}18`, color: CORAL }}
                        >
                          {children}
                        </code>
                      );
                    },
                    pre: ({ children }) => (
                      <pre className="my-3 rounded-lg bg-muted/50 border border-border/40 px-4 py-3.5 overflow-x-auto">
                        {children}
                      </pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="my-4 pl-4 border-l-[3px] text-foreground/60 italic" style={{ borderColor: `${CORAL}80` }}>
                        {children}
                      </blockquote>
                    ),
                    hr: () => <hr className="my-6 border-border/30" />,
                    a: ({ children, href }) => (
                      <a href={href} className="underline underline-offset-2" style={{ color: CORAL }} target="_blank" rel="noreferrer">
                        {children}
                      </a>
                    ),
                    img: ({ src, alt }) => (
                      <img src={src} alt={alt ?? ""} className="my-3 max-w-full rounded-lg border border-border/30" loading="lazy" />
                    ),
                    table: ({ children }) => (
                      <div className="my-4 overflow-x-auto rounded-lg border border-border/40">
                        <table className="w-full text-[13px] border-collapse">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="bg-muted/30 border-b border-border/40">{children}</thead>
                    ),
                    tbody: ({ children }) => <tbody>{children}</tbody>,
                    tr: ({ children }) => (
                      <tr className="border-b border-border/10 last:border-0">{children}</tr>
                    ),
                    th: ({ children }) => (
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="px-3 py-2 border-b border-border/15 text-foreground/75 text-[13px]">
                        {children}
                      </td>
                    ),
                  }}
                >
                  {renderedContent}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                <div className="h-10 w-10 rounded-xl bg-muted/50 flex items-center justify-center">
                  <Eye className="h-4 w-4 text-muted-foreground/40" />
                </div>
                <p className="text-sm text-muted-foreground/50">No content</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
